<template>
  <div class="flex items-center space-x-1 group">
    <div class="relative inline-block" ref="dropdownContainer">
      <router-link
        v-if="!hasDropdown"
        :class="[elementsColor, 'hover:text-lh_red']"
        :to="linkTo || '/products'"
        @click="$emit('select')"
      >
        {{ translateKey(label) }}
      </router-link>

      <button
        v-else
        class="text-sm 2xl:text-base font-thin cursor-pointer flex items-center"
        :class="elementsColor"
        @click="toggleDropdown"
      >
        {{ formatLabel(label) }}
        <chevron-down-icon class="w-3 h-3 ml-1" />
      </button>

      <transition name="slide-fade-dropdown">
        <div
          v-show="hasDropdown && showDropdown"
          class="absolute w-fit whitespace-nowrap shadow-xl text-left flex flex-col
                 bg-white dark:bg-slate-700 z-50 lg:mt-5 justify-center"
        >
          <template v-for="(dropdown, idx) in dropdowns" :key="idx">
            <router-link
              v-if="dropdown.type === 'string'"
              :to="buildSimpleLink(dropdown)"
              :class="elementsColor"
              class="text-sm 2xl:text-base px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-600"
              @click="handleSelect"
            >
              {{ translateKey(dropdown.label) }}
            </router-link>

            <div v-else-if="dropdown.type === 'list'" class="relative">
              <button
                @click="toggleNestedList(idx)"
                class="flex items-center w-full px-3 py-2
                       bg-white dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600"
                :class="elementsColor"
              >
                <span class="text-sm 2xl:text-base capitalize">
                  {{ translateKey(dropdown.label) }}
                </span>
                <chevron-right-icon class="w-3 h-3 ml-2" />
              </button>

              <transition name="slide-fade-nested">
                <div
                  v-show="isNestedListOpen(idx)"
                  class="absolute left-full top-0 ml-1 flex flex-col
                         bg-white dark:bg-slate-700 shadow-lg min-w-full"
                >
                  <router-link
                    v-for="value in dropdown.values"
                    :key="value.label"
                    :to="buildNestedLink(dropdown, value)"
                    :class="elementsColor"
                    class="text-sm 2xl:text-base px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-600"
                    @click="handleSelect"
                  >
                    {{ translateKey(value.label) }}
                  </router-link>
                </div>
              </transition>
            </div>
          </template>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/24/outline';

export default {
  name: 'BaseAnchorTag',

  components: {
    ChevronRightIcon,
    ChevronDownIcon,
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    hasDropdown: {
      type: Boolean,
      default: false,
    },
    linkTo: {
      type: String,
      default: '',
    },
    dropdowns: {
      type: Array,
      default: () => [],
      validator: (items) => items.every((item) => ['string', 'list'].includes(item.type)),
    },
    elementsColor: {
      type: String,
      default: 'text-gray-800 dark:text-white hover:text-lh_red font-thin dark:hover:text-lh_red',
    },
  },

  emits: ['select'],

  data() {
    return {
      showDropdown: false,
      openNestedIndex: null,
    };
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    handleClickOutside(event) {
      const container = this.$refs.dropdownContainer;
      if (container && !container.contains(event.target)) {
        this.closeDropdown();
      }
    },
    toggleDropdown() {
      if (this.showDropdown) {
        this.closeDropdown();
        return;
      }
      this.openDropdown();
    },
    openDropdown() {
      this.showDropdown = true;
      this.openNestedIndex = null;
    },
    closeDropdown() {
      this.showDropdown = false;
      this.openNestedIndex = null;
    },
    toggleNestedList(index) {
      this.openNestedIndex = this.openNestedIndex === index ? null : index;
    },
    isNestedListOpen(index) {
      return this.openNestedIndex === index;
    },
    handleSelect() {
      this.closeDropdown();
      this.$emit('select');
    },
    formatLabel(label) {
      return label.replaceAll('_', ' ');
    },
    translateKey(key) {
      const path = 'frontoffice.navbar';
      const translated = this.$t(`${path}.${key}`);

      // If translation not found, return the original key
      return translated.includes(path) ? key : translated;
    },
    buildSimpleLink(dropdown) {
      const base = '/products';
      const { category, subcategory } = dropdown;

      if (subcategory) {
        return `${base}/${category}/${subcategory}`;
      }
      return `${base}/${category}`;
    },
    buildNestedLink(dropdown, value) {
      const base = '/products';
      const compatibility = this.label;
      const harp = dropdown.label;
      const { category, subcategory } = value;

      return `${base}/${compatibility}/${harp}/${category}/${subcategory}`;
    },
  },
};
</script>

<style scoped>
.slide-fade-dropdown-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-dropdown-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-dropdown-enter-from,
.slide-fade-dropdown-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}

.slide-fade-nested-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-nested-leave-active {
  transition: all 0.15s ease-in;
}

.slide-fade-nested-enter-from,
.slide-fade-nested-leave-to {
  transform: translateX(-8px);
  opacity: 0;
}
</style>
